import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { SvgIcon } from '@valg-shared/utils/svg-icon';
import { SidenavigationLink, SidenavigationLinkSearchResult } from '../sidenavigation/sidenavigation.model';
import { ValgDKSearchComponent } from '../input/valgdk-search/valgdk-search.component';
import { SideNaviationLinkType } from '@shared/clients/api.generated';
import { camelToSnakeCase } from '@valg-shared/utils/string-utils';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SideNavService } from '@valg-shared/services/sidenav.service'
import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';

let NEXT_ID = 0;

@Component({
  selector: 'valg-responsive-menu',
  templateUrl: './responsive-menu.component.html',
  styleUrls: [ './responsive-menu.component.scss' ]
})
export class ResponsiveMenuComponent implements AfterViewInit, OnChanges, OnDestroy{

  @ViewChild('searchfield') searchfield?: ValgDKSearchComponent;
  @ViewChild('navContainer', { static: false }) navContainer?: ElementRef;

  readonly SvgIcon = SvgIcon;
  @Input() button?: HTMLElement;
  @Input() links: SidenavigationLink[] = [];
  @Input() filterLinks: SidenavigationLink[] = [];
  @Input() sidenavTitle?: string;
  @Input() sidenavParentTitle?: string;
  @Input() searchState = false;

  breakpoint = 992;
  _showMenu = false;
  _smallScreen = false;
  filteredLinks?: SidenavigationLinkSearchResult[];
  searchQuery?: string;
  focusTrap!: ConfigurableFocusTrap;


  public get showMenu(){
    return this._showMenu;
  }
  private set showMenu(value: boolean){
    this._showMenu = value;
  }

  public get smallScreen(){
    return this._smallScreen;
  }
  private set smallScreen(value: boolean){
    this._smallScreen = value;
  }

  @HostBinding('id') _id = 'valg-responsive-menu-' + NEXT_ID++;
  @HostBinding('attr.aria-hidden') hidden: string | null = null;
  @HostBinding('role') role = 'complementary';
  @HostBinding('attr.tabindex') get tabindex(): string | null {
    return this._smallScreen && this._showMenu ? '0' : null;
}

  public get id(){
    return this._id;
  }

  constructor(
    private readonly renderer: Renderer2,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly sidenavService: SideNavService,
    private readonly focusTrapFactory: ConfigurableFocusTrapFactory
    ){
      this.resetOverflowOnNavigationEnd();
    }

  ngOnDestroy(): void {
    this.resetOverflow();
    this.focusTrap.destroy();
  }

  async ngAfterViewInit(): Promise<void> {
    this.checkScreenSize();
    if(this.navContainer) {
      this.focusTrap = this.focusTrapFactory.create(this.navContainer.nativeElement);
      this.focusTrap.focusFirstTabbableElement();
    }
    await this.updateState();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if(changes['button']){
      this.checkScreenSize();
      await this.updateState();
    }
  }

  public async show(searchState = false){
    this.searchState = searchState;
    this.sidenavService.openSidenav();
    this.showMenu = true;
    await this.updateState();
    this.renderer.setStyle(document.body, "overflowY", "hidden");
  }

  public async hide(){
    this.searchfield?.reset();
    this.showMenu = false;
    await this.updateState();
    this.searchState = false;
    this.sidenavService.closeSidenav();
    this.resetOverflow();
  }

  checkScreenSize(){
    this.smallScreen = false;
    if(window.innerWidth <= this.breakpoint){
      this.smallScreen = true;
    }
  }

  onSearch(value: string){
    this.searchQuery = value === "" ? undefined : value.toLowerCase();
    if(this.searchQuery !== undefined && this.searchQuery.length >= 2){
      this.filteredLinks = this.findMatches();
    } else{
      this.filteredLinks = undefined;
      this.links.forEach(link => link.expanded = false);
    }
  }

  findMatches(): SidenavigationLinkSearchResult[] {
    let result: SidenavigationLinkSearchResult[] = [];
    let linksToSearch = this.filterLinks;

    for (let i = 0; i < 4; i++) {
      if (linksToSearch.length === 0) {
        break;
      }

      const matches = this.findParentMatches(linksToSearch)
      result.push(
        {
          links: matches,
          navigationType: linksToSearch[0].navigationType
        });
      linksToSearch = linksToSearch.flatMap(x => x.children ?? []);
    }

    return result;
  }

  findParentMatches(links: SidenavigationLink[]): SidenavigationLink[] {
    return links.filter((l) => {
      if (l.title.toLowerCase().includes(this.searchQuery ?? "") &&
        ((l.routerLink && l.routerLink.length > 1) || l.navigationType === SideNaviationLinkType.MultiMemberConstituency)){
        return true;
      }
      return false;
    });
  }

  sidenavChildTitle(sidenavigationLinkType?: SideNaviationLinkType): string{
    let title = "sidenavigation.titles.";
    return sidenavigationLinkType ? title+camelToSnakeCase(sidenavigationLinkType) : "";
  }

  async updateState(){
    if(this.smallScreen){
      this.hidden = this.showMenu ? 'false' : 'true';
    } else{
      this.hidden = null;
      this.showMenu = false;
      this.sidenavService.closeSidenav();
      if(this.focusTrap != undefined)
        {
          this.focusTrap._disable();
        }
    }
    if(this.showMenu){
      this.renderer.addClass(document.body, 'menu-open');
      if(this.navContainer && this.smallScreen) {
        this.focusTrap = this.focusTrapFactory.create(this.navContainer.nativeElement);
        await this.focusTrap.focusFirstTabbableElementWhenReady();
      }
    } else{
      this.renderer.removeClass(document.body, 'menu-open');
    }
    this.cdr.detectChanges();
    this.resetOverflow();
  }

  @HostListener('window:resize')
  async onResize() {
    this.cdr.detectChanges();
    this.checkScreenSize();
    await this.updateState();
  }

  private resetOverflowOnNavigationEnd(): void {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.resetOverflow();
      }
    });
  }

  private resetOverflow(): void {
    this.renderer.setStyle(document.body, "overflowY", "auto");
  }
}
